import { showErrorToast, showSuccessToast } from '@fff-web/fff-ui-shared';
import { apiRequest, getUserCartData, updateUserCart } from '../utils/data';
import env from '../utils/env';

const GROWTH = {
  FETCH_USER_SUBSCRIPTIONS_REQUEST: 'growth/fetch_user_subscriptions_request',
  FETCH_USER_SUBSCRIPTIONS_SUCCESS: 'growth/fetch_user_subscriptions_success',
  FETCH_USER_SUBSCRIPTIONS_FAILURE: 'growth/fetch_user_subscriptions_failure',
  SELECT_PRODUCT_DETAIL: 'growth/select_product_detail',
  UNSELECT_PRODUCT_DETAIL: 'growth/unselect_product_detail',
  CANCEL_USER_SUBSCRIPTION_REQUEST: 'growth/cancel_user_subscription_request',
  CANCEL_USER_SUBSCRIPTION_SUCCESS: 'growth/cancel_user_subscription_success',
  CANCEL_USER_SUBSCRIPTION_FAILURE: 'growth/cancel_user_subscription_failure',
  UPDATE_PRODUCT_SUBSCRIPTION_QUANTITY_REQUEST:
    'growth/update_product_subscription_quantity_request',
  UPDATE_PRODUCT_SUBSCRIPTION_QUANTITY_SUCCESS:
    'growth/update_product_subscription_quantity_success',
  UPDATE_PRODUCT_SUBSCRIPTION_QUANTITY_FAILURE:
    'growth/update_product_subscription_quantity_failure',
  ADD_CUSTOMIZE_SELECTIONS_TO_CART_REQUEST: 'growth/add_customize_selections_to_cart_request',
  ADD_CUSTOMIZE_SELECTIONS_TO_CART_SUCCESS: 'growth/add_customize_selections_to_cart_success',
  ADD_CUSTOMIZE_SELECTIONS_TO_CART_FAILURE: 'growth/add_customize_selections_to_cart_failure',
  SET_USER_CART: 'growth/set_user_cart',
};

const initialState = {
  userSubscriptions: [],
  isLoading: false,
  error: null,
  selectedProduct: {},
  cancelingSubscriptionId: null,
  isCanceling: false,
  cancelError: null,
  updatingSubscriptionId: null,
  isUpdating: false,
  updateError: null,
  userCart: null,
  isAddingCustomizeSelectionsToCart: false,
  addCustomizeSelectionsToCartError: null,
};

export const fetchUserSubscriptionsRequest = () => ({
  type: GROWTH.FETCH_USER_SUBSCRIPTIONS_REQUEST,
});

export const fetchUserSubscriptionsSuccess = (userSubscriptions) => ({
  type: GROWTH.FETCH_USER_SUBSCRIPTIONS_SUCCESS,
  userSubscriptions,
});

export const fetchUserSubscriptionsFailure = (error) => ({
  type: GROWTH.FETCH_USER_SUBSCRIPTIONS_FAILURE,
  error,
});

export const selectProductDetail = (product) => ({
  type: GROWTH.SELECT_PRODUCT_DETAIL,
  product,
});

export const unselectProductDetail = () => ({
  type: GROWTH.UNSELECT_PRODUCT_DETAIL,
});

export const cancelUserSubscriptionRequest = (subscriptionUuid) => ({
  type: GROWTH.CANCEL_USER_SUBSCRIPTION_REQUEST,
  subscriptionUuid,
});

export const cancelUserSubscriptionSuccess = (subscriptionUuid) => ({
  type: GROWTH.CANCEL_USER_SUBSCRIPTION_SUCCESS,
  subscriptionUuid,
});

export const cancelUserSubscriptionFailure = (error) => ({
  type: GROWTH.CANCEL_USER_SUBSCRIPTION_FAILURE,
  error,
});

export const updateProductSubscriptionQuantityRequest = (subscriptionUuid) => ({
  type: GROWTH.UPDATE_PRODUCT_SUBSCRIPTION_QUANTITY_REQUEST,
  subscriptionUuid,
});

export const updateProductSubscriptionQuantitySuccess = (subscriptionUuid, quantity) => ({
  type: GROWTH.UPDATE_PRODUCT_SUBSCRIPTION_QUANTITY_SUCCESS,
  subscriptionUuid,
  quantity,
});

export const updateProductSubscriptionQuantityFailure = (error) => ({
  type: GROWTH.UPDATE_PRODUCT_SUBSCRIPTION_QUANTITY_FAILURE,
  error,
});

export const addCustomizeSelectionsToCartRequest = () => ({
  type: GROWTH.ADD_CUSTOMIZE_SELECTIONS_TO_CART_REQUEST,
});

export const addCustomizeSelectionsToCartSuccess = () => ({
  type: GROWTH.ADD_CUSTOMIZE_SELECTIONS_TO_CART_SUCCESS,
});

export const addCustomizeSelectionsToCartFailure = (error) => ({
  type: GROWTH.ADD_CUSTOMIZE_SELECTIONS_TO_CART_FAILURE,
  error,
});

export const setUserCart = (userCart) => ({
  type: GROWTH.SET_USER_CART,
  userCart,
});

export const getUserSubscriptions = () => async (dispatch) => {
  dispatch(fetchUserSubscriptionsRequest());
  try {
    const response = await fetch(apiRequest('GET', `${env.growth}/v1/subscriptions/self`));
    if (response.status >= 300) {
      throw response.status;
    } else {
      const data = await response.json();
      dispatch(fetchUserSubscriptionsSuccess(data.subscriptions || []));
    }
  } catch (error) {
    dispatch(fetchUserSubscriptionsFailure(error));
  }
};

export const cancelUserSubscription = (subscriptionUuid) => async (dispatch) => {
  dispatch(cancelUserSubscriptionRequest(subscriptionUuid));
  try {
    const response = await fetch(
      apiRequest(
        'PATCH',
        `${env.growth}/v1/subscriptions/self/subscription-uuid/${subscriptionUuid}/cancel`,
      ),
    );
    if (response.status >= 300) {
      throw response.status;
    } else {
      dispatch(cancelUserSubscriptionSuccess(subscriptionUuid));
      // TODO: remove this call to getUserSubscriptions if cancelUserSubscriptionSuccess updates the store correctly
      dispatch(getUserSubscriptions());
    }
  } catch (error) {
    dispatch(cancelUserSubscriptionFailure(error));
  }
};

export const updateSubscriptionQuantity = (payload) => async (dispatch) => {
  const { subscriptionUuid, quantity } = payload;
  dispatch(updateProductSubscriptionQuantityRequest(subscriptionUuid));
  try {
    const response = await fetch(
      apiRequest(
        'PATCH',
        `${env.growth}/v1/subscriptions/self/subscription-uuid/${subscriptionUuid}/quantity`,
        {
          quantity,
        },
      ),
    );
    if (response.status >= 300) {
      throw response.status;
    } else {
      dispatch(updateProductSubscriptionQuantitySuccess(subscriptionUuid, quantity));
      showSuccessToast(
        'reFills Updated',
        'Your quantity has been successfully updated to all future deliveries.',
      );
    }
  } catch (error) {
    dispatch(updateProductSubscriptionQuantityFailure(error));
    showErrorToast(
      'Update Failed',
      'There was an issue updating your quantity. Please try again later.',
    );
  }
};

/**
 * Adds customize selections from a subscription to the user's CUSTOMER (shop) cart.
 *
 * Builds a maui-compatible `CustomizeCartItemPayload[]` from the selections —
 * each item carries `CUSTOMIZE_SALE_UUID` and `CUSTOMIZE_CHOICE_TYPE` in its
 * preferences — then PATCHes the user's customer cart. On success, the cart is
 * re-fetched so consumers see the new `prepaidSubscriptionGroups`.
 *
 * @param {object} params
 * @param {string} params.cartUuid - The user's CUSTOMER cart uuid.
 * @param {string} params.customizeSaleUuid - The customize sale uuid.
 * @param {Array<{
 *   productUuid: string,
 *   saleUuid: string,
 *   catalogUuid: string,
 *   choiceType: 'PRIMARY' | 'ADDITIONAL' | 'INSERT',
 *   quantity?: number,
 * }>} params.selections
 */
export const addCustomizeSelectionsToCustomerCart =
  ({ cartUuid, selections }) =>
  async (dispatch) => {
    dispatch(addCustomizeSelectionsToCartRequest());

    try {
      const validSelections = (selections || []).filter((selection) =>
        Boolean(selection.productUuid),
      );

      if (validSelections.some((selection) => !selection.choiceType)) {
        throw new Error('Cannot add to cart: one or more selections are missing choiceType');
      }

      const cartItems = validSelections.map((selection) => ({
        productUuid: selection.productUuid,
        quantity: selection.quantity > 0 ? selection.quantity : 1,
        saleUuid: selection.saleUuid,
        catalogUuid: selection.catalogUuid,
        cartItemPreferences: {
          CUSTOMIZE_CHOICE_TYPE: selection.choiceType,
        },
      }));

      const response = await updateUserCart(cartUuid, cartItems);

      if (response?.error || response?.success === false) {
        throw response?.error ?? new Error('Failed to add customize selections to cart');
      }

      const refreshedCart = await getUserCartData();
      dispatch(setUserCart(refreshedCart));
      dispatch(addCustomizeSelectionsToCartSuccess());
    } catch (error) {
      dispatch(addCustomizeSelectionsToCartFailure(error));
      showErrorToast(
        'Could not add to cart',
        'There was an issue adding your items to the cart. Please try again later.',
      );
    }
  };

const growthReducer = (state = initialState, action) => {
  switch (action.type) {
    case GROWTH.FETCH_USER_SUBSCRIPTIONS_REQUEST:
      return {
        ...state,
        isLoading: true,
        error: null,
      };

    case GROWTH.FETCH_USER_SUBSCRIPTIONS_SUCCESS:
      return {
        ...state,
        isLoading: false,
        userSubscriptions: action.userSubscriptions,
        error: null,
      };

    case GROWTH.FETCH_USER_SUBSCRIPTIONS_FAILURE:
      return {
        ...state,
        isLoading: false,
        error: action.error,
      };

    case GROWTH.SELECT_PRODUCT_DETAIL:
      return {
        ...state,
        selectedProduct: {
          ...action.product,
        },
      };

    case GROWTH.UNSELECT_PRODUCT_DETAIL:
      return {
        ...state,
        selectedProduct: {},
      };

    case GROWTH.CANCEL_USER_SUBSCRIPTION_REQUEST:
      return {
        ...state,
        cancelingSubscriptionId: action.subscriptionUuid,
        isCanceling: true,
        cancelError: null,
      };

    case GROWTH.CANCEL_USER_SUBSCRIPTION_SUCCESS:
      return {
        ...state,
        cancelingSubscriptionId: null,
        isCanceling: false,
        cancelError: null,
        userSubscriptions: state.userSubscriptions.map((subscription) =>
          subscription.id === action.subscriptionUuid
            ? { ...subscription, cancelled: true, active: false }
            : subscription,
        ),
      };

    case GROWTH.CANCEL_USER_SUBSCRIPTION_FAILURE:
      return {
        ...state,
        cancelingSubscriptionId: null,
        isCanceling: false,
        cancelError: action.error,
      };

    case GROWTH.UPDATE_PRODUCT_SUBSCRIPTION_QUANTITY_REQUEST:
      return {
        ...state,
        updatingSubscriptionId: action.subscriptionUuid,
        isUpdating: true,
        updateError: null,
      };

    case GROWTH.UPDATE_PRODUCT_SUBSCRIPTION_QUANTITY_SUCCESS:
      return {
        ...state,
        updatingSubscriptionId: null,
        isUpdating: false,
        updateError: null,
        userSubscriptions: state.userSubscriptions.map((subscription) =>
          subscription.id === action.subscriptionUuid
            ? { ...subscription, quantity: action.quantity }
            : subscription,
        ),
      };

    case GROWTH.UPDATE_PRODUCT_SUBSCRIPTION_QUANTITY_FAILURE:
      return {
        ...state,
        updatingSubscriptionId: null,
        isUpdating: false,
        updateError: action.error,
      };

    case GROWTH.ADD_CUSTOMIZE_SELECTIONS_TO_CART_REQUEST:
      return {
        ...state,
        isAddingCustomizeSelectionsToCart: true,
        addCustomizeSelectionsToCartError: null,
      };

    case GROWTH.ADD_CUSTOMIZE_SELECTIONS_TO_CART_SUCCESS:
      return {
        ...state,
        isAddingCustomizeSelectionsToCart: false,
        addCustomizeSelectionsToCartError: null,
      };

    case GROWTH.ADD_CUSTOMIZE_SELECTIONS_TO_CART_FAILURE:
      return {
        ...state,
        isAddingCustomizeSelectionsToCart: false,
        addCustomizeSelectionsToCartError: action.error,
      };

    case GROWTH.SET_USER_CART:
      return {
        ...state,
        userCart: action.userCart,
      };

    default:
      return state;
  }
};

export default growthReducer;
